import { AlertOctagon } from "lucide-react";
import { Modal } from "../Modal";
import { ActionButton, GhostButton, FormField, inputClass } from "../Primitives";
import { MultiSelectDropdown } from "../MultiSelectDropdown";
import SearchableSelect from "../SearchableSelect";
import { WEEKDAY_LABELS, isoDate, jsDayToPy } from "./shiftUtils";
import { ShiftTZPreview } from "./ShiftTZPreview";
import DurationChips from "./DurationChips";

export function ShiftFormModal({ form, setForm, onClose, onSave, sites, guardOptions, submitting = false }) {
  const onStartChange = (val) => {
    setForm((prev) => {
      const next = { ...prev, start_time: val };
      if (val && prev.recurrence_days.length === 0) {
        const d = new Date(val);
        next.recurrence_days = [jsDayToPy(d)];
        const end = new Date(d); end.setDate(end.getDate() + 28);
        next.recurrence_end_date = isoDate(end);
      }
      return next;
    });
  };
  const toggleDay = (idx) => {
    setForm((prev) => {
      const has = prev.recurrence_days.includes(idx);
      return {
        ...prev,
        recurrence_days: has
          ? prev.recurrence_days.filter((d) => d !== idx)
          : [...prev.recurrence_days, idx].sort(),
      };
    });
  };
  const presetWeekdays = () => setForm((f) => ({ ...f, recurrence_days: [0, 1, 2, 3, 4] }));
  const presetWeekend = () => setForm((f) => ({ ...f, recurrence_days: [5, 6] }));
  const presetDaily = () => setForm((f) => ({ ...f, recurrence_days: [0, 1, 2, 3, 4, 5, 6] }));

  const selectedSite = form.site_id ? sites.find((s) => s.id === form.site_id) : null;

  return (
    <Modal title="Schedule Shift" onClose={onClose} testId="shift-form-modal">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <FormField label="Officers (leave empty for OPEN shift)">
            <MultiSelectDropdown
              testId="shift-guards"
              label="Leave open · unassigned"
              options={guardOptions}
              selected={form.guard_ids}
              onChange={(ids) => setForm({ ...form, guard_ids: ids })}
              searchable
              triggerClassName="w-full"
              emptyHint="No  match"
            />
          </FormField>
          {form.guard_ids.length === 0 && (
            <div className="text-[10px] uppercase tracking-widest text-[#FF9F0A] mt-1 flex items-center gap-1" data-testid="open-shift-hint">
              <AlertOctagon className="size-3" /> Open shift — no  assigned. Will appear in amber on the roster.
            </div>
          )}
        </div>
        <FormField label="Site">
          {/* iter307 — Searchable single-select. As Iris scales past
              ~15 sites, the native <select> becomes hard to scan; typing
              the site's name filters instantly. */}
          <SearchableSelect
            testId="shift-site-select"
            options={sites.map((s) => ({ value: s.id, label: s.name, hint: s.address || undefined }))}
            value={form.site_id || ""}
            onChange={(v) => setForm({ ...form, site_id: v })}
            placeholder="Select site…"
            emptyHint="No sites match"
          />
        </FormField>
        <div></div>
        <FormField label="Start">
          {/* iter312 — Force `color-scheme: dark` so Chrome / Edge /
              Safari render the native calendar picker indicator on our
              dark background (otherwise it draws black-on-black and
              users think the picker vanished). */}
          <input
            type="datetime-local"
            data-testid="shift-start-input"
            className={inputClass}
            style={{ colorScheme: "dark" }}
            value={form.start_time}
            onChange={(e) => onStartChange(e.target.value)}
          />
        </FormField>
        <FormField label="End">
          <input
            type="datetime-local"
            data-testid="shift-end-input"
            className={inputClass}
            style={{ colorScheme: "dark" }}
            value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
          />
          {/* iter313 — one-tap presets so admins skip typing an end
              time entirely once they've picked the start. */}
          <DurationChips
            startLocal={form.start_time}
            endLocal={form.end_time}
            onPick={(nextEnd) => setForm({ ...form, end_time: nextEnd })}
            testIdPrefix="shift-duration"
          />
        </FormField>

        {/* iter289 — TZ preview (shared component, also used in EditShiftModal). */}
        <ShiftTZPreview
          site={selectedSite}
          startLocal={form.start_time}
          endLocal={form.end_time}
          testIdPrefix="shift-tz-preview"
        />

        <FormField label="Minimum Officers Required">
          <input
            type="number"
            min="0"
            max="20"
            data-testid="shift-min-guards-input"
            className={inputClass}
            value={form.min_guards}
            onChange={(e) => setForm({ ...form, min_guards: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) })}
          />
        </FormField>
        <div className="text-[10px] text-zinc-500 self-end pb-2 leading-snug">
          Cards turn <span className="text-[#FF9F0A] font-bold">amber</span> when fewer than this many  are assigned.
        </div>

        <div className="col-span-2 border-t border-[#27272A] pt-4">
          <div className="label-mute mb-2">Recurrence</div>
          <div className="flex gap-2 mb-3">
            {[
              { v: false, label: "One-time" },
              { v: true, label: "Repeats" },
            ].map((opt) => (
              <button
                key={String(opt.v)}
                type="button"
                data-testid={`recurrence-${opt.v ? "repeats" : "none"}`}
                onClick={() => setForm({ ...form, repeats: opt.v })}
                className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors duration-150 ${
                  form.repeats === opt.v ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]" : "border-[#27272A] text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {form.repeats && (
            <div className="space-y-3 border border-[#27272A] bg-[#0A0A0A] p-4" data-testid="recurrence-config">
              <div>
                <div className="label-mute mb-2">Repeat on</div>
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAY_LABELS.map((lbl, idx) => {
                    const active = form.recurrence_days.includes(idx);
                    return (
                      <button
                        key={lbl}
                        type="button"
                        data-testid={`dow-${lbl.toLowerCase()}`}
                        onClick={() => toggleDay(idx)}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border transition-colors duration-150 ${
                          active ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]" : "border-[#27272A] text-zinc-400 hover:border-zinc-500"
                        }`}
                      >
                        {lbl}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2 mt-2 text-[10px] uppercase tracking-widest">
                  <button type="button" data-testid="preset-weekdays" onClick={presetWeekdays} className="text-[#0A84FF] hover:underline">Weekdays</button>
                  <span className="text-zinc-700">·</span>
                  <button type="button" data-testid="preset-weekend" onClick={presetWeekend} className="text-[#0A84FF] hover:underline">Weekend</button>
                  <span className="text-zinc-700">·</span>
                  <button type="button" data-testid="preset-daily" onClick={presetDaily} className="text-[#0A84FF] hover:underline">Every Day</button>
                </div>
              </div>
              <FormField label="Until (end date)">
                <input
                  type="date"
                  data-testid="recurrence-end-input"
                  className={inputClass}
                  value={form.recurrence_end_date}
                  min={form.start_time ? form.start_time.slice(0, 10) : undefined}
                  max={(() => {
                    // iter288 — 3-year ceiling so the browser's own date
                    // picker blocks impractical selections. Backend also
                    // enforces this via SERIES_MAX_DAYS.
                    const base = form.start_time ? new Date(form.start_time) : new Date();
                    const cap = new Date(base);
                    cap.setFullYear(cap.getFullYear() + 3);
                    const pad = (n) => String(n).padStart(2, "0");
                    return `${cap.getFullYear()}-${pad(cap.getMonth() + 1)}-${pad(cap.getDate())}`;
                  })()}
                  onChange={(e) => setForm({ ...form, recurrence_end_date: e.target.value })}
                />
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  Series can span up to <span className="text-[#0A84FF] font-mono">3 years</span>.
                </div>
              </FormField>
              {/* iter290 — Optional series label so long-running contracts
                  get a friendly tag on every shift card without touching
                  each shift's notes. */}
              <FormField label="Series label (optional)">
                <input
                  type="text"
                  data-testid="create-series-label-input"
                  className={inputClass}
                  placeholder="e.g. Q1 patrol · Acme HQ"
                  value={form.series_label || ""}
                  onChange={(e) => setForm({ ...form, series_label: e.target.value })}
                  maxLength={80}
                />
              </FormField>
            </div>
          )}
        </div>

        <div className="col-span-2">
          <FormField label="Notes">
            <textarea rows={2} className={inputClass} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FormField>
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <GhostButton onClick={onClose} disabled={submitting}>Cancel</GhostButton>
        <ActionButton data-testid="save-shift-button" onClick={onSave} disabled={submitting}>
          {submitting
            ? (form.repeats ? "Scheduling…" : "Saving…")
            : (form.repeats ? "Schedule Series" : "Schedule")}
        </ActionButton>
      </div>
    </Modal>
  );
}
